import React, { ReactNode } from 'react'
import { Link } from 'gatsby'
import { Icon } from '@chakra-ui/icons'
import { Box, Flex, Text, Center, Spacer } from '@chakra-ui/react'
import { IconType } from 'react-icons'
import { useLocation } from '@reach/router'

import { SidebarItem } from '../tabs'
import { UserRoutes } from '~config/routes'
import SharedStack from '~components/shared/wrappers/shared-stack'
import NewTag from '~components/shared/tags/new-tag'
import { FiExternalLink } from 'react-icons/fi'
import { getIsActive } from './helpers'
import { Collapse } from '@chakra-ui/react'
import { RiArrowDownSLine, RiArrowRightSLine } from 'react-icons/ri'
import SidebarItemIcon from './icon'

interface Props {
    isPinned?: boolean
    icon: IconType
    label: string
    to?: string
    badge?: number
    pages?: SidebarItem[]
    isNew?: boolean
    isExternal?: boolean
    isSubPage?: boolean
    otherPaths?: string[]
    submenu: string
    setSubmenu: (value: React.SetStateAction<string>) => void
}

export default function SidebarItemComponent(props: Props) {
    const {
        isPinned = false,
        icon,
        to,
        label,
        badge,
        isNew,
        isExternal,
        isSubPage,
        pages,
        otherPaths,
        submenu,
        setSubmenu,
    } = props

    const { pathname } = useLocation()
    const isActive = getIsActive(to || '/', pathname, pages, otherPaths)

    const isOpen = submenu === label
    const activePage = pages?.find(
        ({ to, otherPaths: pageOtherPaths }) =>
            (to && (to === '/' ? pathname === to : pathname.includes(to))) ||
            Boolean(pageOtherPaths?.some(path => pathname.includes(path))),
    )
    const hasNewPages = pages?.some(({ isNew }) => isNew)
    const badgeCount =
        badge || pages?.reduce((acc, page) => acc + (page.badge || 0), 0)

    return (
        <Box
            w="full"
            borderWidth={isSubPage ? 0 : 1}
            borderColor={isActive || isOpen ? 'gray.700' : 'transparent'}
            _hover={{
                borderColor: 'gray.700',
                bg: isSubPage ? 'gray.700' : 'gray.800',
            }}
            transition="all 0.2s"
            bg={
                isActive || isOpen
                    ? isSubPage
                        ? 'gray.700'
                        : 'gray.800'
                    : 'transparent'
            }
            rounded="lg"
            pl={isPinned && isSubPage ? 2 : 0}
            _groupHover={{
                pl: isSubPage ? 2 : 0,
            }}
        >
            <SidebarItemActionWrapper {...props} isOpen={isOpen}>
                <SharedStack row align="center" spacing={1}>
                    <SidebarItemIcon
                        isActive={isActive}
                        icon={
                            pages && isActive && !isOpen
                                ? activePage?.icon || icon
                                : icon
                        }
                        badgeCount={badgeCount}
                        showIndicator={!isPinned}
                    />
                    <SharedStack
                        row
                        align="center"
                        spacing={1}
                        display={{
                            base: 'flex',
                            lg: isPinned ? 'flex' : 'none',
                        }}
                        opacity={{ base: 1, lg: isPinned ? 1 : 0 }}
                        _groupHover={{ display: 'flex', opacity: 1 }}
                        transition="opacity 0.2s ease-in-out"
                    >
                        <Text
                            noOfLines={1}
                            color={isActive ? 'white' : 'gray.300'}
                            fontWeight={isActive ? 'semibold' : 'normal'}
                            transition="0.2s"
                            fontSize="14px"
                        >
                            {pages && isActive && !isOpen && (
                                <Box
                                    as="span"
                                    fontSize="11px"
                                    fontWeight="normal"
                                    color="gray.300"
                                >
                                    {label} /{' '}
                                </Box>
                            )}
                            {pages && isActive && !isOpen
                                ? activePage?.label
                                : label}
                        </Text>
                        {(isNew || hasNewPages) && (
                            <NewTag ml={1} colorScheme="brand" />
                        )}
                        <Spacer />
                        {!!badgeCount && (
                            <Center
                                bg="brand.500"
                                rounded="full"
                                px={2}
                                mr={!isExternal && !pages?.length ? 2 : 0}
                            >
                                <Text
                                    fontSize="9px"
                                    color="gray.900"
                                    fontWeight="semibold"
                                >
                                    {badgeCount > 9 ? '9+' : badgeCount}
                                </Text>
                            </Center>
                        )}
                        {isExternal && (
                            <Center w="32px" h="32px">
                                <Icon
                                    as={FiExternalLink}
                                    boxSize="13px"
                                    color="gray.400"
                                />
                            </Center>
                        )}
                        {pages && (
                            <Center w="32px" h="32px">
                                <Icon
                                    as={
                                        !isOpen
                                            ? RiArrowRightSLine
                                            : RiArrowDownSLine
                                    }
                                    boxSize="13px"
                                    color="gray.400"
                                />
                            </Center>
                        )}
                    </SharedStack>
                </SharedStack>
            </SidebarItemActionWrapper>
            {pages && (
                <Box as={Collapse} w="full" in={isOpen}>
                    <SharedStack spacing={1} pt={1}>
                        {pages.map((page, index) => (
                            <SidebarItemComponent
                                isPinned={isPinned}
                                key={index}
                                {...page}
                                isSubPage
                                submenu={submenu}
                                setSubmenu={setSubmenu}
                            />
                        ))}
                    </SharedStack>
                </Box>
            )}
        </Box>
    )
}

function SidebarItemActionWrapper({
    to,
    label,
    pages,
    isOpen,
    isSubPage,
    isExternal,
    setSubmenu,
    children,
}: Pick<
    Props,
    'to' | 'label' | 'pages' | 'setSubmenu' | 'isExternal' | 'isSubPage'
> & { isOpen: boolean; children: ReactNode }) {
    if (pages) {
        return (
            <Box
                cursor="pointer"
                onClick={() => {
                    if (isOpen) setSubmenu('none')
                    else setSubmenu(label)
                }}
            >
                {children}
            </Box>
        )
    }

    if (isExternal) {
        return (
            <Box as="a" href={to} target="_blank">
                {children}
            </Box>
        )
    }

    return (
        <Box cursor="pointer" onClick={() => !isSubPage && setSubmenu('none')}>
            <Link to={to || UserRoutes.Home}>{children}</Link>
        </Box>
    )
}
